import * as React from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Settings as SettingsModel, saveSettings } from "../../zegal/settings";
import { ApiError, login } from "../../zegal/api";
import { zegalColors } from "../theme";

interface LoginProps {
  settings: SettingsModel;
  onSignedIn: (next: SettingsModel) => void;
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
  },
  logo: {
    width: "120px",
    height: "auto",
    color: zegalColors.brand,
    marginBottom: tokens.spacingVerticalL,
  },
  card: {
    width: "100%",
    maxWidth: "320px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "3px",
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: zegalColors.textPrimary,
    textAlign: "center",
    marginBottom: tokens.spacingVerticalXS,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase200,
    color: zegalColors.textSecondary,
    textAlign: "center",
    marginBottom: tokens.spacingVerticalS,
  },
  submit: {
    marginTop: tokens.spacingVerticalS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: "center",
    lineHeight: tokens.lineHeightBase200,
  },
});

const LoginPanel: React.FC<LoginProps> = ({ settings, onSignedIn }) => {
  const styles = useStyles();
  const [email, setEmail] = React.useState(settings.username || "");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const canSubmit = email.trim().length > 0 && password.length > 0 && !busy;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { dragonball, username } = await login(
        settings,
        email.toLowerCase().trim(),
        password
      );
      const next: SettingsModel = { ...settings, dragonball, username };
      saveSettings(next);
      onSignedIn(next);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.root}>
      <img src="assets/zegal-logo.svg" alt="Zegal" className={styles.logo} />

      <form className={styles.card} onSubmit={handleSubmit}>
        <div className={styles.title}>Sign in</div>
        <div className={styles.subtitle}>Use your Zegal account to continue.</div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(_, d) => setEmail(d.value)}
            autoComplete="username"
            required
          />
        </Field>

        <Field label="Password">
          <Input
            type="password"
            value={password}
            onChange={(_, d) => setPassword(d.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <Button
          className={styles.submit}
          appearance="primary"
          type="submit"
          disabled={!canSubmit}
        >
          {busy ? "Signing in..." : "Sign in"}
        </Button>

        <div className={styles.hint}>
          Or paste an API key in <strong>Settings</strong> to sign in without a password.
        </div>
      </form>
    </div>
  );
};

export default LoginPanel;
